import { morph } from '@theme/morph';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode } = options ?? {};
    const { url } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId);

      morphSection(sectionId, sectionHTML);
    }

    return sectionHTML;
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);

      if (cachedHTML) return cachedHTML;
    }

    pendingPromise = fetch(sectionUrl).then((response) => {
      return response.text();
    });

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    const sectionHTML = await pendingPromise;
    this.#pendingPromises.delete(sectionUrl);

    this.#cache.set(sectionUrl, sectionHTML);
    return sectionHTML;
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  if (sectionId) {
    url.searchParams.set('section_id', normalizeSectionId(sectionId));
    url.searchParams.sort();
  }

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Maps the price params in the URL back to the matching price range checkbox value.
 * The fallback price filter can't work this out in Liquid, so the checkbox state is restored from the URL.
 * @param {URLSearchParams} params - The URL search params
 * @returns {string} The price_range_option value ('below_100', '101_150', 'above_150') or '' if none match
 */
export function getActivePriceRangeOption(params) {
  const gte = params.get('filter.v.price.gte');
  const lte = params.get('filter.v.price.lte');
  const min = gte ? Number(gte) : null;
  const max = lte ? Number(lte) : null;

  if ((min === null || min === 0) && max === 100) return 'below_100';
  if ((min === 100 || min === 101) && max === 150) return '101_150';
  if (min === 150 && max === null) return 'above_150';
  return '';
}

/**
 * Checks the price range checkbox that matches the URL and unchecks the others
 * @param {ParentNode} root - The element containing the price range checkboxes
 * @param {URLSearchParams} params - The URL search params
 */
export function syncPriceRangeCheckboxes(root, params) {
  const activeOption = getActivePriceRangeOption(params);

  root.querySelectorAll('input[type="checkbox"][name="price_range_option"]').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    const shouldBeChecked = input.value === activeOption;
    input.toggleAttribute('checked', shouldBeChecked);
    input.checked = shouldBeChecked;
  });
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 */
export async function morphSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  // Pre-sync model-view-toggle-wrapper position in newElement to match existingElement
  const existingMvToggle = existingElement.querySelector('.model-view-toggle-wrapper');
  const newMvToggle = newElement.querySelector('.model-view-toggle-wrapper');
  if (existingMvToggle && newMvToggle) {
    if (existingMvToggle.classList.contains('mv-desktop')) {
      const newHost = newElement.querySelector('sorting-filter-component');
      if (newHost) {
        newHost.before(newMvToggle);
        newMvToggle.className = existingMvToggle.className;
        const parent = newHost.parentElement;
        if (parent) parent.classList.add('mv-sort-row');
      }
    } else if (existingMvToggle.classList.contains('mv-mobile')) {
      const newSticky = newElement.querySelector('sticky-facets-toggle');
      if (newSticky) {
        newSticky.after(newMvToggle);
        newMvToggle.className = existingMvToggle.className;
      }
    }
  }

  // Pre-sync drawer dialog open state and scroll position
  const existingDialog = existingElement.querySelector('#filters-drawer dialog');
  const newDialog = newElement.querySelector('#filters-drawer dialog');
  const isDrawerOpen = existingDialog && (existingDialog.open || existingDialog.hasAttribute('open'));
  if (isDrawerOpen && newDialog) {
    newDialog.setAttribute('open', '');
    newDialog.open = true;
  }

  // Preserve open/closed state of details accordions in drawer
  existingElement.querySelectorAll('#filters-drawer details').forEach((oldDet) => {
    if (oldDet.id) {
      const newDet = newElement.querySelector(`#${CSS.escape(oldDet.id)}`);
      if (newDet) {
        if (oldDet.hasAttribute('open')) {
          newDet.setAttribute('open', '');
          newDet.open = true;
        } else {
          newDet.removeAttribute('open');
          newDet.open = false;
        }
      }
    }
  });

  const existingDrawerContent = existingElement.querySelector('.facets--drawer');
  const drawerScrollTop = existingDrawerContent ? existingDrawerContent.scrollTop : 0;

  // Pre-sync input checked state and active label classes in newElement from window.location before morphing
  // This prevents morph from momentarily unchecking active filters and causing visual flicker
  const currentParams = new URLSearchParams(window.location.search);
  syncPriceRangeCheckboxes(newElement, currentParams);
  newElement.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((newInput) => {
    if (newInput instanceof HTMLInputElement && newInput.name && newInput.value) {
      const activeValues = currentParams.getAll(newInput.name);
      let shouldBeChecked = activeValues.includes(newInput.value);
      if (!shouldBeChecked && newInput.name.includes('filter_style')) {
        if (
          (newInput.value === 'Flats' || newInput.value === 'Ballet Flats') &&
          (activeValues.includes('Flats') || activeValues.includes('Ballet Flats'))
        ) {
          shouldBeChecked = true;
        }
      }
      if (shouldBeChecked) {
        newInput.setAttribute('checked', '');
        newInput.checked = true;
        const wrapper = newInput.closest('.facets__style-card-wrapper');
        const label = wrapper?.querySelector('.facets__style-card-label');
        if (label) label.classList.add('is-active');
      } else {
        newInput.removeAttribute('checked');
        newInput.checked = false;
        const wrapper = newInput.closest('.facets__style-card-wrapper');
        const label = wrapper?.querySelector('.facets__style-card-label');
        if (label) label.classList.remove('is-active');
      }
    }
  });

  // Pre-sync mobile filter count bubble in newElement
  let filterCount = 0;
  let hasPriceFilter = false;
  for (const [key, value] of currentParams.entries()) {
    if (!value) continue;
    if (key === 'filter.v.price.gte' || key === 'filter.v.price.lte' || key === 'price_range_option') {
      if (!hasPriceFilter) {
        hasPriceFilter = true;
        filterCount++;
      }
    } else if (key.startsWith('filter.')) {
      filterCount++;
    }
  }

  const toggleButtons = newElement.querySelectorAll('.facets-toggle__button, .sticky-facets-toggle__button');
  toggleButtons.forEach((btn) => {
    let bubble = btn.querySelector('.filter-count-bubble');
    if (filterCount > 0) {
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.id = 'filter-count-bubble-toggle';
        bubble.className = 'filter-count-bubble';
        bubble.innerHTML = `
          <span class="filter-count-bubble__background"></span>
          <span class="filter-count-bubble__text" aria-hidden="true">${filterCount}</span>
        `;
        btn.appendChild(bubble);
      } else {
        const textEl = bubble.querySelector('.filter-count-bubble__text');
        if (textEl) textEl.textContent = filterCount.toString();
        bubble.style.display = '';
      }
    } else if (bubble) {
      bubble.remove();
    }
  });

  // Pre-sync active filter pills in newElement so pills aren't removed and re-added by morph
  const newRemoveContainers = newElement.querySelectorAll('.facets-remove');
  if (newRemoveContainers.length) {
    const activeParams = [];
    let hasAnyActiveFilter = false;
    for (const [key, value] of currentParams.entries()) {
      if ((key.startsWith('filter.') || key === 'price_range_option') && value) {
        hasAnyActiveFilter = true;
        if (key !== 'filter.v.price.gte' && key !== 'filter.v.price.lte') {
          activeParams.push({ key, value });
        }
      }
    }

    newRemoveContainers.forEach((container) => {
      const existingPills = Array.from(container.querySelectorAll('.facets-remove__pill'));
      activeParams.forEach(({ key, value }) => {
        if (value.startsWith('gid://')) return;

        let label = value;
        let matchingInput = null;
        try {
          matchingInput = newElement.querySelector(`input[name="${CSS.escape(key)}"][value="${CSS.escape(value)}"]`) ||
                          document.querySelector(`input[name="${CSS.escape(key)}"][value="${CSS.escape(value)}"]`);
        } catch (e) {}

        if (matchingInput) {
          label = matchingInput.dataset?.label || matchingInput.getAttribute('aria-label') || matchingInput.closest('label')?.textContent?.trim() || value;
        }

        if (label.startsWith('gid://')) return;

        if (key.toLowerCase().includes('availability')) {
          label = value === '1' ? 'In Stock' : (value === '0' ? 'Out of Stock' : value);
        } else if (key.toLowerCase().includes('size')) {
          const cleanSize = value.replace(/^EU\s*/i, '').trim();
          label = `EU (${cleanSize})`;
        }

        const labelLower = label.toLowerCase();
        const alreadyRendered = existingPills.some((pill) => {
          if (pill.dataset?.url) {
            try {
              const pillUrl = new URL(pill.dataset.url, window.location.origin);
              const remainingVals = pillUrl.searchParams.getAll(key);
              if (!remainingVals.includes(value)) return true;
            } catch (e) {}
          }
          const text = pill.textContent.trim().toLowerCase();
          return text.includes(labelLower) || labelLower.includes(text);
        });

        if (!alreadyRendered) {
          const removeUrl = new URL(window.location.href);
          const allVals = removeUrl.searchParams.getAll(key);
          removeUrl.searchParams.delete(key);
          allVals.filter((v) => v !== value).forEach((v) => removeUrl.searchParams.append(key, v));

          const pill = document.createElement('facet-remove-component');
          pill.className = 'pills__pill pills__pill--desktop-small facets-remove__pill';
          pill.dataset.url = removeUrl.toString();
          pill.dataset.filterName = key;
          pill.dataset.filterValue = value;
          pill.tabIndex = 0;
          pill.setAttribute('role', 'button');
          pill.setAttribute('on:click', '/removeFilter?form=');
          pill.setAttribute('on:keydown', '/removeFilter?form=');
          pill.innerHTML = `
            <span>${label}</span>
            <span class="svg-wrapper svg-wrapper--smaller">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L9 9M9 1L1 9" stroke="rgba(0, 0, 0, 0.45)" stroke-width="1.2" stroke-linecap="round"/>
              </svg>
            </span>
            <span class="visually-hidden">Remove</span>
          `;
          const clearComponent = container.querySelector('facet-remove-component:has(.facets__clear-all), facet-remove-component[data-is-clear-all="true"]');
          if (clearComponent) {
            container.insertBefore(pill, clearComponent);
          } else {
            container.appendChild(pill);
          }
        }
      });

      if ((hasAnyActiveFilter || activeParams.length > 0)) {
        let clearAllEl = container.querySelector('.facets__clear-all');
        if (!clearAllEl) {
          const clearUrl = new URL(window.location.href);
          for (const [key] of currentParams.entries()) {
            if (key.startsWith('filter.') || key === 'price_range_option') {
              clearUrl.searchParams.delete(key);
            }
          }
          const clearComponent = document.createElement('facet-remove-component');
          clearComponent.dataset.url = clearUrl.toString();
          clearComponent.dataset.isClearAll = 'true';
          clearComponent.tabIndex = 0;
          clearComponent.setAttribute('role', 'button');
          clearComponent.setAttribute('on:click', '/removeFilter?form=');
          clearComponent.setAttribute('on:keydown', '/removeFilter?form=');
          clearComponent.innerHTML = `
            <button type="button" class="button-unstyled facets__clear-all-link facets__clear-all active" on:click="/removeFilter?form=" on:keydown="/removeFilter?form=">
              Clear all
            </button>
          `;
          container.appendChild(clearComponent);
        } else {
          const parentComponent = clearAllEl.closest('facet-remove-component');
          if (parentComponent) container.appendChild(parentComponent);
        }
      }
    });
  }

  morph(existingElement, newElement);

  if (isDrawerOpen) {
    const updatedDialog = existingElement.querySelector('#filters-drawer dialog');
    if (updatedDialog && !updatedDialog.open) {
      updatedDialog.setAttribute('open', '');
      try {
        updatedDialog.showModal();
      } catch (e) {
        updatedDialog.open = true;
      }
    }
    if (drawerScrollTop > 0) {
      const updatedDrawerContent = existingElement.querySelector('.facets--drawer');
      if (updatedDrawerContent) {
        updatedDrawerContent.scrollTop = drawerScrollTop;
      }
    }
  }
}

export const sectionRenderer = new SectionRenderer();
